import { useCallback, useEffect, useRef, useState } from "react";
import { Download, Eye, File as FileIcon, FileArchive, FileText, FolderOpen, Image as ImageIcon, Pencil, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, BACKEND_URL, errMsg, FILE_FOLDERS, fmtDate, fmtSize } from "../lib/api";
import { EmptyState } from "./ui-bits";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

function fileIcon(mime) {
  if (mime?.startsWith("image/")) return ImageIcon;
  if (mime?.includes("zip") || mime?.includes("compressed")) return FileArchive;
  if (mime?.includes("pdf") || mime?.includes("text") || mime?.includes("document")) return FileText;
  return FileIcon;
}

export function FileList({ projectId, projects = [] }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [folder, setFolder] = useState(FILE_FOLDERS[0]);
  const [uploadPid, setUploadPid] = useState(projectId || "");
  const [renaming, setRenaming] = useState(null);
  const [renameValue, setRenameValue] = useState("");
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(projectId ? `/projects/${projectId}/files` : "/files");
      setFiles(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (projectId) setUploadPid(projectId);
  }, [projectId]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (!uploadPid) return toast.error("Bitte zuerst ein Projekt auswählen");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("folder", folder);
      await api.post(`/projects/${uploadPid}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`„${file.name}" hochgeladen`);
      load();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setUploading(false);
    }
  };

  const remove = async (f) => {
    if (!window.confirm(`Datei „${f.original_name}" wirklich löschen?`)) return;
    try {
      await api.delete(`/files/${f.id}`);
      toast.success("Datei gelöscht");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const saveRename = async () => {
    try {
      await api.put(`/files/${renaming.id}`, { original_name: renameValue, folder: renaming.folder });
      toast.success("Datei umbenannt");
      setRenaming(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const openFile = (f, preview = false) => {
    window.open(`${BACKEND_URL}/api/files/${f.id}/${preview ? "preview" : "download"}`, "_blank");
  };

  const grouped = FILE_FOLDERS.map((fo) => ({ folder: fo, files: files.filter((f) => f.folder === fo) })).filter((g) => g.files.length);
  const otherFiles = files.filter((f) => !FILE_FOLDERS.includes(f.folder));
  if (otherFiles.length) grouped.push({ folder: "Sonstige", files: otherFiles });

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center gap-2">
        {!projectId && (
          <Select value={uploadPid} onValueChange={setUploadPid}>
            <SelectTrigger data-testid="file-upload-project" className="w-56 rounded-xl bg-white"><SelectValue placeholder="Projekt wählen" /></SelectTrigger>
            <SelectContent>
              {projects.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.client_name ? `${p.client_name} – ` : ""}{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        <Select value={folder} onValueChange={setFolder}>
          <SelectTrigger data-testid="file-upload-folder" className="w-48 rounded-xl bg-white"><SelectValue /></SelectTrigger>
          <SelectContent>
            {FILE_FOLDERS.map((f) => (
              <SelectItem key={f} value={f}>{f}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <input ref={inputRef} type="file" className="hidden" onChange={upload} data-testid="file-upload-input" />
        <Button
          data-testid="file-upload-button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          className="rounded-xl bg-[#1D225C] text-white hover:bg-[#2a3070]"
        >
          <Upload className="mr-1.5 h-4 w-4" /> {uploading ? "Lädt hoch…" : "Datei hochladen"}
        </Button>
      </div>

      {loading ? (
        <p className="py-8 text-center text-sm text-[#777]">Laden…</p>
      ) : files.length === 0 ? (
        <EmptyState icon={FolderOpen} title="Noch keine Dateien" hint="Logos, Bilder, Texte und Dokumente direkt zum Projekt hochladen." testid="files-empty" />
      ) : (
        <div className="space-y-5">
          {grouped.map((g) => (
            <div key={g.folder}>
              <p className="mb-2 text-xs font-semibold uppercase tracking-[0.15em] text-[#B88A4A]">{g.folder}</p>
              <div className="overflow-hidden rounded-2xl border border-[#1D225C]/10 bg-white shadow-sm">
                {g.files.map((f, i) => {
                  const Icon = fileIcon(f.mime);
                  const isImage = f.mime?.startsWith("image/");
                  return (
                    <div
                      key={f.id}
                      data-testid={`file-row-${f.id}`}
                      className={`flex flex-wrap items-center gap-3 px-4 py-3 transition-colors hover:bg-[#F7F6F2] ${i ? "border-t border-[#1D225C]/5" : ""}`}
                    >
                      <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-[#1D225C]/5">
                        <Icon className="h-4 w-4 text-[#1D225C]" />
                      </span>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium text-[#1D225C]">{f.original_name}</p>
                        <p className="text-xs text-[#777]">
                          {fmtSize(f.size)} · {f.uploader_name} · {fmtDate(f.created_at)}
                          {!projectId && f.project_name ? ` · ${f.project_name}` : ""}
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        {isImage && (
                          <button data-testid={`file-preview-${f.id}`} onClick={() => openFile(f, true)} className="rounded-lg p-1.5 text-[#1D225C] hover:bg-[#1D225C]/5" title="Vorschau">
                            <Eye className="h-4 w-4" />
                          </button>
                        )}
                        <button data-testid={`file-download-${f.id}`} onClick={() => openFile(f)} className="rounded-lg p-1.5 text-[#1D225C] hover:bg-[#1D225C]/5" title="Herunterladen">
                          <Download className="h-4 w-4" />
                        </button>
                        <button data-testid={`file-rename-${f.id}`} onClick={() => { setRenaming(f); setRenameValue(f.original_name); }} className="rounded-lg p-1.5 text-[#1D225C] hover:bg-[#1D225C]/5" title="Umbenennen">
                          <Pencil className="h-4 w-4" />
                        </button>
                        <button data-testid={`file-delete-${f.id}`} onClick={() => remove(f)} className="rounded-lg p-1.5 text-red-500 hover:bg-red-50" title="Löschen">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!renaming} onOpenChange={(v) => !v && setRenaming(null)}>
        <DialogContent className="max-w-sm rounded-2xl" data-testid="file-rename-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-lg text-[#1D225C]">Datei umbenennen</DialogTitle>
          </DialogHeader>
          <Input data-testid="file-rename-input" value={renameValue} onChange={(e) => setRenameValue(e.target.value)} onKeyDown={(e) => e.key === "Enter" && saveRename()} />
          <div className="mt-2">
            <Select value={renaming?.folder} onValueChange={(v) => setRenaming((r) => ({ ...r, folder: v }))}>
              <SelectTrigger data-testid="file-rename-folder" className="rounded-xl"><SelectValue /></SelectTrigger>
              <SelectContent>
                {FILE_FOLDERS.map((f) => (
                  <SelectItem key={f} value={f}>{f}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" data-testid="file-rename-cancel" onClick={() => setRenaming(null)} className="rounded-xl">Abbrechen</Button>
            <Button data-testid="file-rename-submit" onClick={saveRename} className="rounded-xl bg-[#1D225C] text-white hover:bg-[#2a3070]">Speichern</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
